import type { Shape } from "@gettactic/map-builder/src/types";
import {
  fromShapeLocal,
  getFrameBox,
  getShapeLocalBox,
  normalizeRect,
  rotatePoint,
  shapeFrameTransform,
  toShapeLocal
} from "../document/bounds";
import { rotateShapeAboutPoint, shapesInBox } from "../document/operations";
import { rotateShapes, scaleShapes } from "../document/transformOps";
import {
  clearScratchTransform,
  setScratchTransform,
  setTranslate
} from "../gesture/domTransform";
import { isModifierKey, isPrimaryButton } from "../gesture/pointer";
import {
  HANDLE_CURSORS,
  type HandleKind,
  type ResizeHandle
} from "../render/handles";
import type { Point, Rect } from "../types";
import {
  beginGeometryDrag,
  cancelGeometryDrag,
  clickGeometryHandle,
  contextMenuGeometryHandle,
  doubleClickGeometryHandle,
  endGeometryDrag,
  type GeometryDrag,
  type GeometryHandle,
  parseGeometryHandle,
  updateGeometryDrag
} from "./geometryGestures";
import { createFrameScheduler, type Tool, type ToolContext } from "./Tool";

/** Screen pixels the pointer must travel before a press becomes a drag. */
const DRAG_THRESHOLD = 3;
const MIN_SIZE = 4;

type Gesture =
  | { phase: "idle" }
  | {
      phase: "pressed";
      client: Point;
      world: Point;
      shapeId: string | null;
      handle: HandleKind | null;
      geometry: GeometryHandle | null;
      isAdditive: boolean;
      isSpotsOnly: boolean;
      isDuplicate: boolean;
    }
  | {
      phase: "moving";
      ids: string[];
      nodes: SVGGElement[];
      origin: Point;
      delta: Point;
    }
  | {
      phase: "marquee";
      origin: Point;
      isAdditive: boolean;
      isSpotsOnly: boolean;
    }
  | {
      phase: "resizing";
      id: string;
      node: SVGGElement;
      handle: ResizeHandle;
      startBox: Rect;
      box: Rect;
      isCentred: boolean;
    }
  | {
      phase: "rotating";
      id: string;
      node: SVGGElement;
      centre: Point;
      /** The pivot in the shape's own frame; the commit keeps it in place. */
      localCentre: Point;
      startAngle: number;
      startRotation: number;
      rotation: number;
    }
  | { phase: "geometry"; drag: GeometryDrag }
  /** Several shapes scaled about the anchor opposite the grabbed handle. */
  | {
      phase: "group-resizing";
      ids: string[];
      nodes: { id: string; node: SVGGElement }[];
      handle: ResizeHandle;
      /** Box and pivot in the selection frame (rotated by `rotation`). */
      startBox: Rect;
      box: Rect;
      pivot: Point;
      rotation: number;
    }
  /** Several shapes turned together about the selection centre. */
  | {
      phase: "group-rotating";
      ids: string[];
      nodes: SVGGElement[];
      centre: Point;
      startAngle: number;
      spin: number;
    };

const angleOf = (centre: Point, point: Point): number =>
  (Math.atan2(point.y - centre.y, point.x - centre.x) * 180) / Math.PI;

const resizeBox = (
  start: Rect,
  handle: ResizeHandle,
  delta: Point,
  keepAspect: boolean
): Rect => {
  let { x, y, width, height } = start;
  if (handle.includes("e")) width = start.width + delta.x;
  if (handle.includes("w")) {
    width = start.width - delta.x;
    x = start.x + delta.x;
  }
  if (handle.includes("s")) height = start.height + delta.y;
  if (handle.includes("n")) {
    height = start.height - delta.y;
    y = start.y + delta.y;
  }
  if (
    keepAspect &&
    start.width > 0 &&
    start.height > 0 &&
    handle.length === 2
  ) {
    const ratio = start.width / start.height;
    if (Math.abs(width / ratio) > Math.abs(height)) height = width / ratio;
    else width = height * ratio;
    if (handle.includes("w")) x = start.x + start.width - width;
    if (handle.includes("n")) y = start.y + start.height - height;
  }
  if (width < MIN_SIZE) {
    if (handle.includes("w")) x = start.x + start.width - MIN_SIZE;
    width = MIN_SIZE;
  }
  if (height < MIN_SIZE) {
    if (handle.includes("n")) y = start.y + start.height - MIN_SIZE;
    height = MIN_SIZE;
  }
  return { x, y, width, height };
};

/** Store patch that gives a shape the new bounding box. Circles are
 *  centred on (x, y); everything else is anchored at its top-left. */
const patchForBox = (shape: Shape, box: Rect): Partial<Shape> => {
  const rounded = {
    x: Math.round(box.x),
    y: Math.round(box.y),
    width: Math.round(box.width),
    height: Math.round(box.height)
  };
  if (shape.type === "circle") {
    return {
      x: rounded.x + rounded.width / 2,
      y: rounded.y + rounded.height / 2,
      width: rounded.width,
      height: rounded.height,
      radius: Math.round(Math.max(rounded.width, rounded.height) / 2)
    };
  }
  return rounded;
};

/**
 * Click, shift-click, marquee (cmd/ctrl for spots only), move with
 * snapping, alt-drag duplicate, resize and rotate from the handles. Every
 * pointer move writes to the DOM; the store hears about it on pointer up.
 */
export const createSelectTool = (): Tool => {
  let gesture: Gesture = { phase: "idle" };
  let context: ToolContext | null = null;

  const frame = createFrameScheduler<{
    client: Point;
    shiftKey: boolean;
    altKey: boolean;
    metaKey: boolean;
    ctrlKey: boolean;
  }>(({ client, shiftKey, altKey, metaKey, ctrlKey }) => {
    if (!context) return;
    const world = context.toWorld(client);
    if (gesture.phase === "moving") {
      const raw = {
        x: world.x - gesture.origin.x,
        y: world.y - gesture.origin.y
      };
      // Alt during the drag shows the gaps to the neighbours (precision mode).
      const snapped = context.snap.snapMove(gesture.ids, raw, {
        showDistances: altKey
      });
      gesture.delta = snapped.delta;
      setTranslate(gesture.nodes, snapped.delta);
      context.overlay.set({
        selectionTransform: `translate(${snapped.delta.x} ${snapped.delta.y})`,
        guides: snapped.guides,
        readout: {
          anchor: world,
          lines: [`${signed(snapped.delta.x)}, ${signed(snapped.delta.y)}`]
        }
      });
      return;
    }
    if (gesture.phase === "marquee") {
      context.overlay.set({ marquee: normalizeRect(gesture.origin, world) });
      return;
    }
    if (gesture.phase === "resizing") {
      const shape = context.store.getState().shapes[gesture.id];
      if (!shape) return;
      // Resize happens in the shape's own frame, so the pointer is taken
      // there first and the scratch scale is wrapped in that frame.
      const start = gesture.startBox;
      const anchor = handleAnchor(start, gesture.handle);
      const local = toShapeLocal(shape, world);
      const pointerDelta = {
        x:
          context.snap.snapToGrid(local.x) -
          anchorOpposite(start, gesture.handle).x,
        y:
          context.snap.snapToGrid(local.y) -
          anchorOpposite(start, gesture.handle).y
      };
      gesture.box = resizeBox(start, gesture.handle, pointerDelta, shiftKey);
      const scaleX = start.width > 0 ? gesture.box.width / start.width : 1;
      const scaleY = start.height > 0 ? gesture.box.height / start.height : 1;
      const frame = shapeFrameTransform(shape);
      const rotation = shape.rotation || 0;
      const unframe = frame ? `rotate(${-rotation} ${shape.x} ${shape.y})` : "";
      setScratchTransform(
        [gesture.node],
        `${frame ?? ""} translate(${anchor.x} ${anchor.y}) scale(${scaleX} ${scaleY}) translate(${-anchor.x} ${-anchor.y}) ${unframe}`.trim()
      );
      context.overlay.set({
        selectionTransform: null,
        marquee: null,
        readout: { anchor: world, lines: [sizeLabel(gesture.box)] }
      });
      return;
    }
    if (gesture.phase === "group-resizing") {
      const { startBox, pivot, rotation } = gesture;
      const anchor = handleAnchor(startBox, gesture.handle);
      // Pointer into the selection frame, resize there, preview through
      // the frame so a turned selection scales along its own axes.
      const local = rotatePoint(world, pivot, -rotation);
      const pointerDelta = {
        x:
          context.snap.snapToGrid(local.x) -
          anchorOpposite(startBox, gesture.handle).x,
        y:
          context.snap.snapToGrid(local.y) -
          anchorOpposite(startBox, gesture.handle).y
      };
      gesture.box = resizeBox(startBox, gesture.handle, pointerDelta, shiftKey);
      const sx = startBox.width > 0 ? gesture.box.width / startBox.width : 1;
      const sy = startBox.height > 0 ? gesture.box.height / startBox.height : 1;
      const frame = rotation ? `rotate(${rotation} ${pivot.x} ${pivot.y})` : "";
      const unframe = rotation
        ? `rotate(${-rotation} ${pivot.x} ${pivot.y})`
        : "";
      const transform =
        `${frame} translate(${anchor.x} ${anchor.y}) scale(${sx} ${sy}) translate(${-anchor.x} ${-anchor.y}) ${unframe}`.trim();
      setScratchTransform(
        gesture.nodes.map((entry) => entry.node),
        transform
      );
      context.overlay.set({
        selectionTransform: transform,
        readout: { anchor: world, lines: [sizeLabel(gesture.box)] }
      });
      return;
    }
    if (gesture.phase === "group-rotating") {
      const raw = angleOf(gesture.centre, world) - gesture.startAngle;
      gesture.spin =
        (shiftKey ? Math.round(raw / 15) * 15 : Math.round(raw)) || 0;
      const transform = `rotate(${gesture.spin} ${gesture.centre.x} ${gesture.centre.y})`;
      setScratchTransform(gesture.nodes, transform);
      context.overlay.set({
        selectionTransform: transform,
        readout: { anchor: world, lines: [`${gesture.spin}°`] }
      });
      return;
    }
    if (gesture.phase === "geometry") {
      updateGeometryDrag(context, gesture.drag, world, {
        altKey,
        metaKey,
        ctrlKey
      });
      return;
    }
    if (gesture.phase === "rotating") {
      const raw =
        gesture.startRotation +
        angleOf(gesture.centre, world) -
        gesture.startAngle;
      gesture.rotation =
        (shiftKey ? Math.round(raw / 15) * 15 : Math.round(raw)) || 0;
      const state = context.store.getState();
      const shape = state.shapes[gesture.id];
      if (!shape) return;
      const spin = gesture.rotation - (shape.rotation || 0);
      setScratchTransform(
        [gesture.node],
        `rotate(${spin} ${gesture.centre.x} ${gesture.centre.y})`
      );
      context.overlay.set({
        readout: {
          anchor: world,
          lines: [`${normalizeDegrees(gesture.rotation)}°`]
        }
      });
    }
  });

  const finishGesture = () => {
    frame.cancel();
    if (gesture.phase === "moving") clearScratchTransform(gesture.nodes);
    if (gesture.phase === "resizing" || gesture.phase === "rotating") {
      clearScratchTransform([gesture.node]);
    }
    if (gesture.phase === "group-resizing") {
      clearScratchTransform(gesture.nodes.map((entry) => entry.node));
    }
    if (gesture.phase === "group-rotating")
      clearScratchTransform(gesture.nodes);
    gesture = { phase: "idle" };
    context?.overlay.reset();
    context?.setCursor(null);
  };

  return {
    kind: "select",
    cursor: "default",

    onPointerDown(toolContext, event) {
      context = toolContext;
      if (!isPrimaryButton(event)) return;
      const rawHandle = toolContext.hitHandle(event.target);
      const geometry = rawHandle ? parseGeometryHandle(rawHandle) : null;
      const handle: HandleKind | null =
        !geometry && rawHandle && rawHandle in HANDLE_CURSORS
          ? (rawHandle as HandleKind)
          : null;
      const shapeId =
        handle || geometry ? null : toolContext.hitShape(event.target);
      const client = { x: event.clientX, y: event.clientY };
      if (geometry && event.button === 2) {
        const selected = toolContext.store.getState().selection[0];
        if (selected)
          contextMenuGeometryHandle(toolContext, selected, geometry);
        return;
      }
      gesture = {
        phase: "pressed",
        client,
        world: toolContext.toWorld(client),
        shapeId,
        handle,
        geometry,
        isAdditive: event.shiftKey,
        isSpotsOnly: isModifierKey(event),
        isDuplicate: event.altKey
      };
      if (handle || geometry) return;

      const { store } = toolContext;
      const state = store.getState();
      const shape = shapeId ? state.shapes[shapeId] : null;
      if (!shape) return;
      if (shape.locked) {
        // Locked shapes can be selected (to unlock them) but never dragged.
        store
          .getState()
          .setSelection(
            event.shiftKey ? toggle(state.selection, shape.id) : [shape.id]
          );
        gesture = { phase: "idle" };
        return;
      }
      if (event.shiftKey) {
        store.getState().setSelection(toggle(state.selection, shape.id));
      } else if (!state.selection.includes(shape.id)) {
        store.getState().setSelection([shape.id]);
      }
    },

    onPointerMove(toolContext, event) {
      context = toolContext;
      const client = { x: event.clientX, y: event.clientY };
      if (gesture.phase === "pressed") {
        const travelled = Math.hypot(
          client.x - gesture.client.x,
          client.y - gesture.client.y
        );
        if (travelled < DRAG_THRESHOLD) return;
        startDrag(toolContext, gesture, event.shiftKey);
      }
      if (gesture.phase === "idle") {
        const handle = toolContext.hitHandle(event.target);
        toolContext.setCursor(
          handle && handle in HANDLE_CURSORS
            ? HANDLE_CURSORS[handle as HandleKind]
            : null
        );
        return;
      }
      frame.schedule({
        client,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey
      });
    },

    onPointerUp(toolContext, event) {
      context = toolContext;
      const { store } = toolContext;
      const state = store.getState();
      if (gesture.phase === "pressed") {
        if (gesture.geometry) {
          const selected = state.selection[0];
          if (selected) {
            clickGeometryHandle(toolContext, selected, gesture.geometry, {
              altKey: event.altKey,
              shiftKey: event.shiftKey
            });
          }
          finishGesture();
          return;
        }
        // A click without travel: a press on empty space clears, a press on
        // an already-selected shape (without shift) selects just that one.
        if (!gesture.shapeId && !gesture.handle) {
          store.getState().setSelection([]);
        } else if (gesture.shapeId && !gesture.isAdditive) {
          const shape = state.shapes[gesture.shapeId];
          if (shape && !shape.locked && state.selection.length > 1) {
            store.getState().setSelection([gesture.shapeId]);
          }
        }
        finishGesture();
        return;
      }
      if (gesture.phase === "moving") {
        // Flush the last frame so the commit matches what is on screen.
        frame.cancel();
        const world = toolContext.toWorld({
          x: event.clientX,
          y: event.clientY
        });
        const snapped = toolContext.snap.snapMove(gesture.ids, {
          x: world.x - gesture.origin.x,
          y: world.y - gesture.origin.y
        });
        const ids = gesture.ids;
        finishGesture();
        store
          .getState()
          .moveShapes(
            ids,
            Math.round(snapped.delta.x),
            Math.round(snapped.delta.y)
          );
        return;
      }
      if (gesture.phase === "marquee") {
        const world = toolContext.toWorld({
          x: event.clientX,
          y: event.clientY
        });
        const box = normalizeRect(gesture.origin, world);
        const hits = shapesInBox(
          { shapes: state.shapes, order: state.order },
          box,
          { spotsOnly: gesture.isSpotsOnly, excludeZones: !state.ui.showZones }
        );
        const next = gesture.isAdditive
          ? [
              ...state.selection,
              ...hits.filter((id) => !state.selection.includes(id))
            ]
          : hits;
        finishGesture();
        store.getState().setSelection(next);
        return;
      }
      if (gesture.phase === "resizing") {
        const shape = state.shapes[gesture.id];
        const box = gesture.box;
        const id = gesture.id;
        finishGesture();
        if (shape) {
          // The box is in the shape's frame; its origin maps back to world
          // because the shape rotates about that origin.
          const patch = patchForBox(shape, box);
          const origin = fromShapeLocal(shape, {
            x: patch.x ?? shape.x,
            y: patch.y ?? shape.y
          });
          store.getState().updateShape(id, {
            ...patch,
            x: Math.round(origin.x),
            y: Math.round(origin.y)
          });
        }
        return;
      }
      if (gesture.phase === "rotating") {
        const shape = state.shapes[gesture.id];
        const id = gesture.id;
        const rotation = gesture.rotation;
        const localCentre = gesture.localCentre;
        finishGesture();
        if (shape)
          store
            .getState()
            .updateShape(
              id,
              rotateShapeAboutPoint(shape, rotation, localCentre)
            );
        return;
      }
      if (gesture.phase === "group-resizing") {
        const { ids, startBox, box, handle, pivot, rotation } = gesture;
        const anchor = handleAnchor(startBox, handle);
        const sx = startBox.width > 0 ? box.width / startBox.width : 1;
        const sy = startBox.height > 0 ? box.height / startBox.height : 1;
        finishGesture();
        if (sx !== 1 || sy !== 1) {
          store
            .getState()
            .commit((document) =>
              scaleShapes(document, ids, anchor, sx, sy, { pivot, rotation })
            );
        }
        return;
      }
      if (gesture.phase === "group-rotating") {
        const { ids, centre, spin } = gesture;
        finishGesture();
        if (spin !== 0) {
          const chrome = store.getState().chrome;
          store
            .getState()
            .commit((document) => rotateShapes(document, ids, centre, spin));
          // The frame turns with the shapes so the box stays attached.
          store.getState().setChrome({
            selectionFrameRotation: chrome.selectionFrameRotation + spin
          });
        }
        return;
      }
      if (gesture.phase === "geometry") {
        frame.cancel();
        const world = toolContext.toWorld({
          x: event.clientX,
          y: event.clientY
        });
        updateGeometryDrag(toolContext, gesture.drag, world, {
          altKey: event.altKey,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey
        });
        endGeometryDrag(toolContext, gesture.drag);
        finishGesture();
      }
    },

    onDoubleClick(toolContext, event) {
      context = toolContext;
      // The pointer was captured on the first press, so the event target
      // is the svg; hit-test the point instead.
      const target = toolContext.elementAt({
        x: event.clientX,
        y: event.clientY
      });
      const rawHandle = toolContext.hitHandle(target);
      const geometry = rawHandle ? parseGeometryHandle(rawHandle) : null;
      const state = toolContext.store.getState();
      const selected = state.selection[0];
      if (geometry && selected) {
        doubleClickGeometryHandle(toolContext, selected, geometry, {
          altKey: event.altKey
        });
        return;
      }
      // Double-click on text opens the inline editor.
      const id = toolContext.hitShape(target);
      const shape = id ? state.shapes[id] : undefined;
      if (shape?.type === "text" && !shape.locked) {
        state.setSelection([shape.id]);
        state.setChrome({ highlightedShape: { shape, targetId: shape.id } });
      }
    },

    onKeyDown(toolContext, event) {
      if (event.key === "Escape" && gesture.phase !== "idle") {
        context = toolContext;
        if (gesture.phase === "geometry")
          cancelGeometryDrag(toolContext, gesture.drag);
        finishGesture();
        return true;
      }
      return false;
    },

    cancel(toolContext) {
      context = toolContext;
      if (gesture.phase === "geometry")
        cancelGeometryDrag(toolContext, gesture.drag);
      finishGesture();
    }
  };

  function startDrag(
    toolContext: ToolContext,
    pressed: Extract<Gesture, { phase: "pressed" }>,
    shiftKey: boolean
  ) {
    const { store } = toolContext;
    const state = store.getState();

    if (pressed.geometry) {
      const selected = state.selection[0];
      const drag = selected
        ? beginGeometryDrag(toolContext, selected, pressed.geometry)
        : null;
      gesture = drag ? { phase: "geometry", drag } : { phase: "idle" };
      return;
    }

    if (pressed.handle && state.selection.length > 1) {
      const ids = state.selection.filter((id) => !state.shapes[id]?.locked);
      const nodes = ids
        .map((id) => ({ id, node: toolContext.shapeNode(id) }))
        .filter(
          (entry): entry is { id: string; node: SVGGElement } => !!entry.node
        );
      const rotation = state.chrome.selectionFrameRotation;
      const frame = getFrameBox(
        ids
          .map((id) => state.shapes[id])
          .filter((shape): shape is Shape => !!shape),
        rotation
      );
      if (!frame || nodes.length === 0) {
        gesture = { phase: "idle" };
        return;
      }
      if (pressed.handle === "rotate") {
        // The frame box centre, back in world coordinates.
        const centre = rotatePoint(
          {
            x: frame.box.x + frame.box.width / 2,
            y: frame.box.y + frame.box.height / 2
          },
          frame.pivot,
          rotation
        );
        gesture = {
          phase: "group-rotating",
          ids,
          nodes: nodes.map((entry) => entry.node),
          centre,
          startAngle: angleOf(centre, pressed.world),
          spin: 0
        };
      } else {
        gesture = {
          phase: "group-resizing",
          ids,
          nodes,
          handle: pressed.handle,
          startBox: frame.box,
          box: frame.box,
          pivot: frame.pivot,
          rotation
        };
      }
      toolContext.setCursor(HANDLE_CURSORS[pressed.handle]);
      return;
    }

    if (pressed.handle) {
      const id = state.selection[0];
      const shape = id ? state.shapes[id] : undefined;
      const node = id ? toolContext.shapeNode(id) : null;
      if (!shape || !node) {
        gesture = { phase: "idle" };
        return;
      }
      const box = getShapeLocalBox(shape);
      if (pressed.handle === "rotate") {
        const localCentre = {
          x: box.x + box.width / 2,
          y: box.y + box.height / 2
        };
        const centre = fromShapeLocal(shape, localCentre);
        gesture = {
          phase: "rotating",
          id,
          node,
          centre,
          localCentre,
          startAngle: angleOf(centre, pressed.world),
          startRotation: shape.rotation || 0,
          rotation: shape.rotation || 0
        };
      } else {
        gesture = {
          phase: "resizing",
          id,
          node,
          handle: pressed.handle,
          startBox: box,
          box,
          isCentred: shiftKey
        };
      }
      toolContext.setCursor(HANDLE_CURSORS[pressed.handle]);
      return;
    }

    if (!pressed.shapeId) {
      gesture = {
        phase: "marquee",
        origin: pressed.world,
        isAdditive: pressed.isAdditive,
        isSpotsOnly: pressed.isSpotsOnly
      };
      return;
    }

    let ids = state.selection.filter((id) => !state.shapes[id]?.locked);
    if (pressed.isDuplicate) {
      // Alt-drag: the copies move, the originals stay where they are.
      ids = store.getState().duplicateSelection({ x: 0, y: 0 });
    }
    const nodes = ids
      .map((id) => toolContext.shapeNode(id))
      .filter((node): node is SVGGElement => !!node);
    gesture = {
      phase: "moving",
      ids,
      nodes,
      origin: pressed.world,
      delta: { x: 0, y: 0 }
    };
    toolContext.setCursor("move");
  }
};

const signed = (value: number): string => {
  const rounded = Math.round(value);
  return rounded > 0 ? `+${rounded}` : String(rounded);
};

const sizeLabel = (box: Rect): string =>
  `${Math.round(box.width)} × ${Math.round(box.height)}`;

const normalizeDegrees = (degrees: number): number =>
  ((Math.round(degrees) % 360) + 360) % 360;

const toggle = (selection: string[], id: string): string[] =>
  selection.includes(id)
    ? selection.filter((selected) => selected !== id)
    : [...selection, id];

/** Corner or edge that stays put while a handle is dragged. */
const handleAnchor = (box: Rect, handle: ResizeHandle): Point => ({
  x: handle.includes("w")
    ? box.x + box.width
    : handle.includes("e")
      ? box.x
      : box.x,
  y: handle.includes("n")
    ? box.y + box.height
    : handle.includes("s")
      ? box.y
      : box.y
});

/** The point the pointer grabbed: the handle's own position. */
const anchorOpposite = (box: Rect, handle: ResizeHandle): Point => ({
  x: handle.includes("w")
    ? box.x
    : handle.includes("e")
      ? box.x + box.width
      : box.x + box.width / 2,
  y: handle.includes("n")
    ? box.y
    : handle.includes("s")
      ? box.y + box.height
      : box.y + box.height / 2
});
